import classNames from "classnames";
import { useEffect, useRef, useState } from "react";
import { useLanguagePreference } from "../hooks/useLanguagePreference";
import { useTranslation } from "../hooks/useTranslation";
import type { SupportedLanguage } from "../i18n";
import { languageOptions } from "./languageOptions";
import styles from "./LanguageSwitcher.module.css";

export function LanguageSwitcher() {
  const { t } = useTranslation();
  const { currentLanguage, isSavingLanguage, updateLanguage } = useLanguagePreference();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) {
      return;
    }

    function handlePointerDown(event: PointerEvent) {
      if (!rootRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    }

    document.addEventListener("pointerdown", handlePointerDown);

    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
    };
  }, [open]);

  const activeOption = languageOptions.find((option) => option.value === currentLanguage);

  if (!activeOption) {
    return null;
  }

  const ActiveFlag = activeOption.Flag;

  async function handleLanguageChange(language: SupportedLanguage) {
    await updateLanguage(language);
    setOpen(false);
  }

  return (
    <div ref={rootRef} className={styles["root"]}>
      <button
        type="button"
        className={styles["trigger"]}
        aria-label={`${t("nav.language")}: ${t(activeOption.labelKey)}`}
        aria-expanded={open}
        aria-haspopup="menu"
        disabled={isSavingLanguage}
        onClick={() => setOpen((isOpen) => !isOpen)}
      >
        <ActiveFlag className={styles["flagIcon"]} />
      </button>

      {open && (
        <div className={styles["panel"]} role="menu" aria-label={t("nav.language")}>
          {languageOptions.map((option) => (
            <button
              key={option.value}
              type="button"
              role="menuitemradio"
              aria-checked={currentLanguage === option.value}
              disabled={isSavingLanguage}
              className={classNames(
                styles["option"],
                currentLanguage === option.value ? styles["optionActive"] : undefined,
              )}
              onClick={() => void handleLanguageChange(option.value)}
            >
              <option.Flag className={styles["flagIcon"]} />
              <span className={styles["label"]}>{t(option.labelKey)}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
